import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useEffect,
} from 'react';

export interface DebugSection {
  id: string;
  content: ReactNode;
  order?: number;
}

// Context for stable methods (won't trigger re-renders when sections change)
interface DeveloperPanelMethodsContextType {
  registerSection: (section: DebugSection) => void;
  unregisterSection: (id: string) => void;
  updateSection: (
    id: string,
    updates: Partial<Omit<DebugSection, 'id'>>,
  ) => void;
}

// Context for sections state (only DevPanel subscribes to this)
interface DeveloperPanelSectionsContextType {
  sections: DebugSection[];
}

const DeveloperPanelMethodsContext = createContext<
  DeveloperPanelMethodsContextType | undefined
>(undefined);

const DeveloperPanelSectionsContext = createContext<
  DeveloperPanelSectionsContextType | undefined
>(undefined);

// Use this hook when you only need methods (won't re-render when sections change)
const useDeveloperPanelMethods = () => {
  const context = useContext(DeveloperPanelMethodsContext);
  if (!context) {
    throw new Error(
      'useDeveloperPanelMethods must be used within a DeveloperPanelProvider',
    );
  }
  return context;
};

// Use this hook when you need to read sections (will re-render when sections change)
export const useDeveloperPanelSections = () => {
  const context = useContext(DeveloperPanelSectionsContext);
  if (!context) {
    throw new Error(
      'useDeveloperPanelSections must be used within a DeveloperPanelProvider',
    );
  }
  return context;
};

interface DeveloperPanelProviderProps {
  children: ReactNode;
}

export const DeveloperPanelProvider = ({
  children,
}: DeveloperPanelProviderProps) => {
  const [sections, setSections] = useState<DebugSection[]>([]);

  const registerSection = useCallback((section: DebugSection) => {
    setSections((prev) => {
      // Remove existing section with same id if it exists
      const filtered = prev.filter((s) => s.id !== section.id);
      // Add new section and sort by order
      return [...filtered, section].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      );
    });
  }, []);

  const unregisterSection = useCallback((id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const updateSection = useCallback(
    (id: string, updates: Partial<Omit<DebugSection, 'id'>>) => {
      setSections((prev) =>
        prev.map((section) =>
          section.id === id ? { ...section, ...updates } : section,
        ),
      );
    },
    [],
  );

  // Methods context value is stable (callbacks are memoized with useCallback)
  const methodsValue = useMemo<DeveloperPanelMethodsContextType>(
    () => ({
      registerSection,
      unregisterSection,
      updateSection,
    }),
    [registerSection, unregisterSection, updateSection],
  );

  // Sections context value changes when sections change
  const sectionsValue = useMemo<DeveloperPanelSectionsContextType>(
    () => ({ sections }),
    [sections],
  );

  return (
    <DeveloperPanelMethodsContext.Provider value={methodsValue}>
      <DeveloperPanelSectionsContext.Provider value={sectionsValue}>
        {children}
      </DeveloperPanelSectionsContext.Provider>
    </DeveloperPanelMethodsContext.Provider>
  );
};

// Convenience hook for components to register debug sections
// Uses the methods-only context so it won't re-render when sections change
export const useDebugSection = (
  section: Omit<DebugSection, 'id'> & { id?: string },
  deps: any[] = [],
) => {
  // Use methods-only context to avoid re-renders when sections state changes
  const { registerSection, unregisterSection } = useDeveloperPanelMethods();
  const generatedId = useState(
    () => `debug-${Math.random().toString(36).substring(2, 9)}`,
  )[0];
  const sectionId = section.id || generatedId;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    registerSection({
      ...section,
      id: sectionId,
    });

    return () => {
      unregisterSection(sectionId);
    };
  }, deps);

  return sectionId;
};
